import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Utensils, Bike, Clock, CheckCircle2, Circle, Loader2, Package, ArrowLeft, Phone, Star, Gift, Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import RideMap from "../components/RideMap";
import { pushSupported, notifPermission } from "../lib/push";

// Suivi livraison en direct Nold Go — carte Leaflet + timeline + position livreur.
// WebSocket temps réel avec fallback polling toutes les 15 s si le WS échoue.
export default function FoodOrderTracking() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(() => notifPermission() === "granted");
  const [pushBusy, setPushBusy] = useState(false);
  const timerRef = useRef();
  const wsRef = useRef(null);

  const enableNotifications = async () => {
    if (!pushSupported()) { toast.error("Votre navigateur ne supporte pas les notifications push."); return; }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Vous devez autoriser les notifications."); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data: keyRes } = await api.get("/push/vapid-public-key");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const key = keyRes.key;
        const padding = "=".repeat((4 - (key.length % 4)) % 4);
        const b64 = (key + padding).replace(/-/g, "+").replace(/_/g, "/");
        const raw = window.atob(b64);
        const arr = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
      }
      await api.post(`/food/order/${orderId}/push-subscribe`, sub.toJSON ? sub.toJSON() : sub);
      setPushEnabled(true);
      toast.success("Notifications activées 🔔 — nous vous préviendrons à chaque étape.");
    } catch (e) {
      toast.error("Impossible d'activer les notifications.");
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/food/order/${orderId}/tracking`);
        if (cancelled) return;
        setData(data);
        setError("");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? "Commande introuvable ou expirée." : "Impossible de charger le suivi.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    // 1er fetch immédiat (HTTP) — remplit l'écran instantanément
    load();

    // 2) Tentative WebSocket temps réel — push toutes les 3 s côté serveur.
    //    Si le WS échoue (proxy, indisponibilité), on garde le polling HTTP toutes les 15 s.
    let wsAlive = false;
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      if (base) {
        const ws = new WebSocket(`${base}/api/food/order/${orderId}/ws-tracking`);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          if (cancelled) return;
          try {
            const payload = JSON.parse(ev.data);
            if (payload && !payload.error) { setData(payload); setError(""); wsAlive = true; }
          } catch { /* ignore */ }
        };
        ws.onopen = () => { wsAlive = true; };
        ws.onclose = () => { wsAlive = false; };
        ws.onerror = () => { wsAlive = false; };
      }
    } catch { /* WS non supporté : on reste en polling */ }

    // Polling HTTP en filet de sécurité (15 s) — arrêté si WS actif
    timerRef.current = setInterval(() => { if (!wsAlive) load(); }, 15000);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F26419]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-6">
        <div className="text-center max-w-sm" data-testid="tracking-error">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-100 grid place-items-center">
            <Package className="w-8 h-8 text-red-500" />
          </div>
          <p className="mt-4 font-head font-bold text-xl text-[#1A1A1A]">{error}</p>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-[#F26419] font-semibold text-sm">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  const d = data || {};
  const pickup = d.restaurant?.lat != null ? { lat: d.restaurant.lat, lng: d.restaurant.lng } : null;
  const dest = d.delivery?.lat != null ? { lat: d.delivery.lat, lng: d.delivery.lng } : null;
  const driver = d.driver?.lat != null ? { lat: d.driver.lat, lng: d.driver.lng } : null;
  const showMap = pickup || dest;

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="food-order-tracking">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">Nold Go · Suivi de livraison</div>
            <h1 className="mt-1 font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">Où en est votre commande ?</h1>
          </div>
          <Link to="/" className="text-[#75807A] hover:text-[#1A1A1A] text-sm font-medium inline-flex items-center gap-1" data-testid="tracking-home">
            <ArrowLeft className="w-4 h-4" /> Accueil
          </Link>
        </div>

        {/* Statut principal */}
        <div className="rounded-3xl bg-[#1A1A1A] text-white px-5 py-4 flex items-center justify-between gap-3" data-testid="tracking-status">
          <span className="text-sm sm:text-base font-semibold flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${d.is_terminal ? "bg-emerald-400" : "bg-[#F26419] animate-pulse"}`} />
            {d.status_label || d.status}
          </span>
          {d.driver?.eta_min != null && !d.is_terminal && (
            <span className="text-xs sm:text-sm text-white/80 inline-flex items-center gap-1">
              <Clock className="w-4 h-4" /> ~{d.driver.eta_min} min
            </span>
          )}
        </div>

        {/* Push notifications (guest client) */}
        {!d.is_terminal && !pushEnabled && pushSupported() && (
          <button
            onClick={enableNotifications}
            disabled={pushBusy}
            data-testid="tracking-enable-push"
            className="mt-3 w-full h-11 rounded-xl bg-white border-2 border-[#F26419] text-[#F26419] font-bold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#F26419] hover:text-white disabled:opacity-60 transition-colors"
          >
            {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Activer les alertes (même onglet fermé)
          </button>
        )}
        {pushEnabled && !d.is_terminal && (
          <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2 text-xs text-emerald-700 inline-flex items-center gap-1.5" data-testid="tracking-push-on">
            <Bell className="w-3.5 h-3.5" /> Notifications activées — vous serez prévenu(e) à chaque étape.
          </div>
        )}

        {/* Carte */}
        {showMap && (
          <div className="mt-4" data-testid="tracking-map">
            <RideMap
              center={pickup || dest || undefined}
              pickup={pickup}
              dest={dest}
              driver={driver || undefined}
              height={320}
            />
          </div>
        )}

        {/* Adresses */}
        <div className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-5 space-y-3">
          <div className="flex items-start gap-3" data-testid="tracking-restaurant">
            <div className="w-9 h-9 shrink-0 rounded-full bg-[#FFF3E6] grid place-items-center">
              <Utensils className="w-4 h-4 text-[#F26419]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold text-[#75807A] uppercase tracking-wide">Restaurant</div>
              <div className="font-head font-bold text-[#1A1A1A] truncate">{d.restaurant?.name || "—"}</div>
              {d.restaurant?.address && (
                <div className="text-sm text-[#3B423E] mt-0.5">{d.restaurant.address}</div>
              )}
            </div>
          </div>
          <div className="border-t border-[#F0EBE1]" />
          <div className="flex items-start gap-3" data-testid="tracking-delivery">
            <div className="w-9 h-9 shrink-0 rounded-full bg-[#FFF3E6] grid place-items-center">
              <MapPin className="w-4 h-4 text-[#F26419]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold text-[#75807A] uppercase tracking-wide">Livraison à</div>
              <div className="text-sm text-[#1A1A1A]">{d.delivery?.address || "—"}</div>
            </div>
          </div>
        </div>

        {/* Livreur (si assigné) OU Recherche coursier en cours */}
        {d.driver ? (
          <div className={`mt-4 rounded-3xl bg-white border-2 p-5 ${d.driver.arrived ? "border-emerald-500 shadow-lg shadow-emerald-500/20" : "border-[#F26419]"}`} data-testid="tracking-driver">
            {d.driver.arrived && (
              <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-bold animate-pulse" data-testid="driver-arrived-badge">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Votre livreur est arrivé
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 shrink-0 rounded-full grid place-items-center ${d.driver.arrived ? "bg-emerald-100" : "bg-[#F26419]/15"}`}>
                <Bike className={`w-5 h-5 ${d.driver.arrived ? "text-emerald-600" : "text-[#F26419]"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold text-[#75807A] uppercase tracking-wide">Votre livreur</div>
                <div className="font-head font-bold text-[#1A1A1A] truncate flex items-center gap-1.5">
                  {d.driver.name}
                  {d.driver.rating != null && d.driver.rating > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-[#75807A] font-semibold">
                      <Star className="w-3 h-3 fill-yellow-400 stroke-yellow-500" /> {d.driver.rating.toFixed(1)}
                    </span>
                  )}
                </div>
                {d.driver.distance_km != null && !d.driver.arrived && (
                  <div className="text-xs text-[#75807A] mt-0.5">à {d.driver.distance_km} km · ~{d.driver.eta_min} min</div>
                )}
              </div>
              {d.driver.phone && (
                <a
                  href={`tel:${d.driver.phone}`}
                  data-testid="tracking-driver-call"
                  className={`shrink-0 inline-flex items-center gap-1.5 h-11 px-4 rounded-full font-bold text-sm text-white shadow-md ${d.driver.arrived ? "bg-emerald-600 hover:bg-emerald-700 animate-pulse" : "bg-[#F26419] hover:bg-[#D94D0E]"}`}
                >
                  <Phone className="w-4 h-4" /> Appeler
                </a>
              )}
            </div>
          </div>
        ) : d.driver_search ? (
          <div className={`mt-4 rounded-3xl border-2 p-5 ${d.driver_search.status === "searching" ? "bg-[#FFF3E6] border-[#F26419]" : "bg-amber-50 border-amber-400"}`} data-testid="tracking-driver-search">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 shrink-0 rounded-full grid place-items-center ${d.driver_search.status === "searching" ? "bg-white" : "bg-amber-100"}`}>
                {d.driver_search.status === "searching" ? (
                  <Loader2 className="w-5 h-5 text-[#F26419] animate-spin" />
                ) : (
                  <Bike className="w-5 h-5 text-amber-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#75807A]">
                  {d.driver_search.status === "searching" ? "Recherche coursier" : "Aucun coursier disponible"}
                </div>
                <div className="font-head font-bold text-[#1A1A1A]">
                  {d.driver_search.message}
                </div>
                {d.driver_search.status === "searching" && d.driver_search.online_count > 0 && (
                  <div className="text-xs text-[#75807A] mt-0.5">
                    🛵 {d.driver_search.online_count} coursier{d.driver_search.online_count > 1 ? "s" : ""} en ligne autour
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Timeline */}
        <div className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-5" data-testid="tracking-timeline">
          <div className="text-[11px] font-bold text-[#75807A] uppercase tracking-wide mb-3">Étapes</div>
          <ol className="space-y-2.5">
            {(d.timeline || []).map((s) => (
              <li key={s.key} className="flex items-center gap-3" data-testid={`step-${s.key}`}>
                {s.state === "done" ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                ) : s.state === "current" ? (
                  <div className="w-5 h-5 rounded-full bg-[#F26419] grid place-items-center shrink-0">
                    <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  </div>
                ) : (
                  <Circle className="w-5 h-5 text-[#E2DFD8] shrink-0" />
                )}
                <span className={`text-sm ${s.state === "current" ? "font-bold text-[#1A1A1A]" : s.state === "done" ? "text-[#3B423E]" : "text-[#9BA39C]"}`}>
                  {s.label}
                </span>
              </li>
            ))}
          </ol>
        </div>

        {/* Photo de preuve de livraison (si le livreur en a pris une) */}
        {d.status === "delivered" && d.proof_photo_url && (
          <div className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-5" data-testid="tracking-proof">
            <div className="text-[11px] font-bold text-[#75807A] uppercase tracking-wide mb-2 inline-flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" /> Photo de la livraison
            </div>
            <img src={d.proof_photo_url} alt="Preuve de livraison" className="rounded-2xl w-full max-h-72 object-contain bg-[#F0EBE1]" />
          </div>
        )}

        {/* Notation + Pourboire (visible uniquement après livraison, une seule fois) */}
        {d.status === "delivered" && !d.rated && (
          <RatingCard orderId={orderId} onRated={() => window.location.reload()} driverName={d.driver?.name} restaurantName={d.restaurant?.name} />
        )}

        {/* Détail commande */}
        {(d.items || []).length > 0 && (
          <div className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-5" data-testid="tracking-items">
            <div className="text-[11px] font-bold text-[#75807A] uppercase tracking-wide mb-3">Votre commande</div>
            <ul className="space-y-1.5">
              {d.items.map((it, i) => (
                <li key={i} className="flex justify-between text-sm">
                  <span className="text-[#1A1A1A]"><b>{it.qty}×</b> {it.name}</span>
                  <span className="text-[#75807A]">{(it.price_eur * it.qty).toFixed(2)} €</span>
                </li>
              ))}
            </ul>
            {d.total_eur != null && (
              <div className="mt-3 pt-3 border-t border-[#F0EBE1] flex justify-between font-bold">
                <span>Total</span>
                <span className="text-[#F26419]">{d.total_eur.toFixed(2)} €</span>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-center text-xs text-[#75807A]">Suivi actualisé automatiquement toutes les 15 secondes · Nold Go</div>
      </div>
    </div>
  );
}


// Notation + pourboire — affichée uniquement quand status=delivered et !rated.
function RatingCard({ orderId, onRated, driverName, restaurantName }) {
  const [driverRating, setDriverRating] = useState(0);
  const [restoRating, setRestoRating] = useState(0);
  const [tipEur, setTipEur] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (driverRating === 0 && restoRating === 0 && tipEur === 0) {
      toast.error("Merci d'indiquer au moins une note ou un pourboire.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/food/order/${orderId}/rate`, {
        driver_rating: driverRating || null,
        resto_rating: restoRating || null,
        tip_eur: tipEur,
        comment: comment.trim(),
      });
      toast.success("Merci pour votre retour ! 🎉");
      onRated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer votre note.");
    } finally { setSubmitting(false); }
  };

  const Stars = ({ value, onChange, testId }) => (
    <div className="flex gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? 0 : n)}
          data-testid={`${testId}-${n}`}
          className="p-1 hover:scale-110 transition-transform"
        >
          <Star className={`w-8 h-8 ${n <= value ? "fill-yellow-400 stroke-yellow-500" : "fill-transparent stroke-[#E2DFD8]"}`} />
        </button>
      ))}
    </div>
  );

  const tipOptions = [0, 1, 2, 3, 5];

  return (
    <div className="mt-4 rounded-3xl bg-gradient-to-br from-[#FFF3E6] to-[#FDF8F0] border-2 border-[#F26419]/40 p-5" data-testid="rating-card">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#F26419] text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
        <Gift className="w-3.5 h-3.5" /> Votre avis compte
      </div>
      <h3 className="mt-2 font-head font-black text-lg text-[#1A1A1A]">
        Notez votre expérience Nold Go
      </h3>
      <div className="mt-4 space-y-4">
        <div>
          <div className="text-sm font-semibold text-[#1A1A1A] mb-1.5">
            Votre livreur {driverName ? <span className="font-normal text-[#75807A]">· {driverName}</span> : ""}
          </div>
          <Stars value={driverRating} onChange={setDriverRating} testId="rating-driver" />
        </div>
        <div>
          <div className="text-sm font-semibold text-[#1A1A1A] mb-1.5">
            Le restaurant {restaurantName ? <span className="font-normal text-[#75807A]">· {restaurantName}</span> : ""}
          </div>
          <Stars value={restoRating} onChange={setRestoRating} testId="rating-resto" />
        </div>
        <div>
          <div className="text-sm font-semibold text-[#1A1A1A] mb-1.5">Pourboire pour votre livreur</div>
          <div className="flex flex-wrap gap-2" data-testid="tip-options">
            {tipOptions.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setTipEur(v)}
                data-testid={`tip-${v}`}
                className={`h-10 px-4 rounded-full font-bold text-sm transition-colors ${
                  tipEur === v ? "bg-[#F26419] text-white" : "bg-white border border-[#E2DFD8] text-[#1A1A1A] hover:border-[#F26419]"
                }`}
              >
                {v === 0 ? "Aucun" : `${v} €`}
              </button>
            ))}
            <input
              type="number"
              min="0"
              max="50"
              step="0.5"
              value={tipEur === 0 || tipOptions.includes(tipEur) ? "" : tipEur}
              onChange={(e) => setTipEur(Math.max(0, Math.min(50, parseFloat(e.target.value) || 0)))}
              placeholder="Autre"
              data-testid="tip-custom"
              className="h-10 w-24 px-3 rounded-full border border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm"
            />
          </div>
        </div>
        <div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Commentaire (facultatif)"
            rows={2}
            maxLength={500}
            data-testid="rating-comment"
            className="w-full px-3 py-2 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm"
          />
        </div>
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="rating-submit"
          className="w-full h-12 rounded-xl bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Envoyer mon avis
        </button>
      </div>
    </div>
  );
}
